import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/layout/Navbar';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, 
  Package, 
  User, 
  MapPin, 
  CreditCard, 
  Clock, 
  CheckCircle,
  Truck,
  AlertCircle,
  FileText,
  ChevronRight,
  Loader2,
  Mail,
  Phone,
  DollarSign,
  Calendar,
  Hash,
  Shield,
  Save,
  RefreshCw,
  Edit3,
  X,
  XCircle,
  TrendingDown,
  TrendingUp
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status configuration
const STATUS_CONFIG = {
  'draft': { 
    label: 'Draft', 
    color: 'bg-gray-100 text-gray-600 border-gray-300',
    icon: Clock
  },
  'awaiting_shipment': { 
    label: 'Awaiting Shipment', 
    color: 'bg-blue-100 text-blue-800 border-blue-300',
    icon: Clock
  },
  'received': { 
    label: 'Cards Received', 
    color: 'bg-yellow-100 text-yellow-800 border-yellow-300',
    icon: Package
  },
  'payment_processed': { 
    label: 'Payment Processed', 
    color: 'bg-orange-100 text-orange-800 border-orange-300',
    icon: CreditCard
  },
  'grading_complete': { 
    label: 'Grading Complete', 
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: CheckCircle
  },
  'id_and_encapsulation': { 
    label: 'Encapsulating', 
    color: 'bg-purple-100 text-purple-800 border-purple-300',
    icon: Package
  },
  'shipped': { 
    label: 'Shipped', 
    color: 'bg-green-100 text-green-800 border-green-300',
    icon: Truck
  },
  'complete': { 
    label: 'Complete', 
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: CheckCircle
  }
};

const getStatusConfig = (status) => {
  return STATUS_CONFIG[status] || { 
    label: status, 
    color: 'bg-gray-100 text-gray-800 border-gray-300',
    icon: Clock
  };
};

const AdminSubmissionDetailPage = () => {
  const { submissionId } = useParams();
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  
  const [submission, setSubmission] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [actionMessage, setActionMessage] = useState(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [trackingNumber, setTrackingNumber] = useState('');
  const [notesSaved, setNotesSaved] = useState(false);
  
  // Adjust Quantity Modal State
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [adjustNewCount, setAdjustNewCount] = useState('');
  const [adjustReason, setAdjustReason] = useState('');
  const [adjustNotes, setAdjustNotes] = useState('');
  const [adjustCalculation, setAdjustCalculation] = useState(null);
  const [adjustLoading, setAdjustLoading] = useState(false);
  const [adjustError, setAdjustError] = useState(null);

  // MGP Modal State
  const [showMgpModal, setShowMgpModal] = useState(false);
  const [mgpCardsBelowThreshold, setMgpCardsBelowThreshold] = useState('');
  const [mgpCalculation, setMgpCalculation] = useState(null);
  const [mgpLoading, setMgpLoading] = useState(false);
  const [mgpError, setMgpError] = useState(null);

  useEffect(() => {
    if (!isAdmin()) {
      navigate('/dashboard', { state: { error: "You don't have permission to access the admin panel" } });
      return;
    }
    fetchSubmission();
  }, [submissionId, isAdmin, navigate]);

  const fetchSubmission = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const response = await axios.get(`${API}/admin/submissions/${submissionId}`);
      setSubmission(response.data);
      setAdminNotes(response.data.admin_notes || '');
      setTrackingNumber(response.data.tracking_number || '');
    } catch (err) {
      console.error('Failed to fetch submission:', err);
      if (err.response?.status === 404) {
        setError('Submission not found');
      } else if (err.response?.status === 403) {
        setError('You do not have permission to view this submission');
      } else {
        setError('Failed to load submission details');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleAction = async (action, endpoint, successMessage) => {
    setActionLoading(action);
    setActionMessage(null);
    try {
      const response = await axios.post(`${API}/admin/submissions/${submissionId}/${endpoint}`);
      setActionMessage({ type: 'success', text: successMessage || response.data.message });
      await fetchSubmission();
    } catch (err) {
      console.error(`Action ${action} failed:`, err);
      setActionMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || `Failed to ${action.toLowerCase()}`
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleMarkReceived = () => handleAction('mark-received', 'mark-received', 'Cards marked as received');
  const handleProcessPayment = () => handleAction('process-payment', 'process-payment', 'Payment processed successfully');
  const handleRetryPayment = () => handleAction('retry-payment', 'retry-payment', 'Payment processed successfully');
  const handleMarkGradingComplete = () => handleAction('mark-grading-complete', 'mark-grading-complete', 'Grading marked as complete');
  const handleMarkComplete = () => handleAction('mark-complete', 'mark-complete', 'Submission marked as complete');

  const handleMarkShipped = async () => {
    if (!trackingNumber.trim()) {
      setActionMessage({ type: 'error', text: 'Please enter a tracking number' });
      return;
    }
    setActionLoading('mark-shipped');
    setActionMessage(null);
    try {
      const response = await axios.post(`${API}/admin/submissions/${submissionId}/mark-shipped`, {
        tracking_number: trackingNumber.trim()
      });
      setActionMessage({ type: 'success', text: response.data.message });
      await fetchSubmission();
    } catch (err) {
      console.error('Mark shipped failed:', err);
      setActionMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to mark as shipped'
      });
    } finally {
      setActionLoading(null);
    }
  };

  const handleSaveNotes = async () => {
    setActionLoading('save-notes');
    try {
      await axios.patch(`${API}/admin/submissions/${submissionId}/notes`, {
        admin_notes: adminNotes
      });
      setNotesSaved(true);
      setTimeout(() => setNotesSaved(false), 3000);
    } catch (err) {
      console.error('Save notes failed:', err);
      setActionMessage({ 
        type: 'error', 
        text: err.response?.data?.detail || 'Failed to save notes'
      });
    } finally {
      setActionLoading(null);
    }
  };

  // Adjust Quantity Modal Functions
  const openAdjustModal = () => {
    setShowAdjustModal(true);
    setAdjustNewCount(submission?.card_count?.toString() || '');
    setAdjustReason('');
    setAdjustNotes('');
    setAdjustCalculation(null);
    setAdjustError(null);
  };

  const closeAdjustModal = () => {
    setShowAdjustModal(false);
    setAdjustNewCount('');
    setAdjustReason('');
    setAdjustNotes('');
    setAdjustCalculation(null);
    setAdjustError(null);
  };

  const calculateAdjustment = async (newCount) => {
    // Allow 0 for return-only/voucher redemption scenarios
    if (newCount === '' || isNaN(newCount) || newCount < 0 || newCount > 1000) {
      setAdjustCalculation(null);
      return;
    }
    
    if (parseInt(newCount) === submission?.card_count) {
      setAdjustCalculation(null);
      return;
    }

    try {
      const response = await axios.post(
        `${API}/admin/submissions/${submissionId}/calculate-adjustment?new_card_count=${newCount}`
      );
      setAdjustCalculation(response.data);
      setAdjustError(null);
    } catch (err) {
      console.error('Calculate adjustment failed:', err);
      setAdjustError(err.response?.data?.detail || 'Failed to calculate adjustment');
      setAdjustCalculation(null);
    }
  };

  const handleAdjustCountChange = (value) => {
    setAdjustNewCount(value);
    if (value && !isNaN(value)) {
      calculateAdjustment(parseInt(value));
    } else {
      setAdjustCalculation(null);
    }
  };

  const handleAdjustAndProcess = async () => {
    // Allow 0 for return-only scenarios, but require reason
    if (adjustNewCount === '' || adjustNewCount === null || !adjustReason) return;
    
    const newCount = parseInt(adjustNewCount);
    if (isNaN(newCount) || newCount < 0) return;
    
    if (newCount === submission?.card_count) {
      setAdjustError('New quantity must be different from original');
      return;
    }

    // Show confirmation for significant changes
    if (adjustCalculation?.is_significant_change) {
      if (!window.confirm(`This is a significant adjustment (${adjustCalculation.percentage_change}% change). Are you sure you want to proceed?`)) {
        return;
      }
    }

    setAdjustLoading(true);
    setAdjustError(null);
    
    try {
      const response = await axios.post(`${API}/admin/submissions/${submissionId}/adjust-quantity`, {
        new_card_count: newCount,
        reason: adjustReason,
        notes: adjustNotes || null
      });
      
      if (response.data.success) {
        setActionMessage({ 
          type: 'success', 
          text: response.data.message 
        });
        closeAdjustModal();
        await fetchSubmission();
      } else {
        // Payment failed after adjustment
        setActionMessage({ 
          type: 'error', 
          text: response.data.message 
        });
        closeAdjustModal();
        await fetchSubmission();
      }
    } catch (err) {
      console.error('Adjust quantity failed:', err);
      setAdjustError(err.response?.data?.detail || 'Failed to adjust quantity');
    } finally {
      setAdjustLoading(false);
    }
  };

  const ADJUSTMENT_REASONS = [
    "Customer shipped fewer cards than listed",
    "Customer shipped more cards than listed",
    "Damaged cards excluded from grading",
    "Customer request",
    "Other"
  ];

  // MGP Functions
  const calculateMgp = async (cardsBelowThreshold) => {
    if (!cardsBelowThreshold || isNaN(cardsBelowThreshold)) {
      setMgpCalculation(null);
      return;
    }
    
    try {
      const response = await axios.get(
        `${API}/admin/submissions/${submissionId}/mgp/calculate`,
        { params: { cards_below_threshold: parseInt(cardsBelowThreshold) } }
      );
      setMgpCalculation(response.data);
      setMgpError(null);
    } catch (err) {
      console.error('MGP calculation failed:', err);
      setMgpError(err.response?.data?.detail || 'Failed to calculate MGP');
      setMgpCalculation(null);
    }
  };

  const handleMgpCardsBelowChange = (value) => {
    setMgpCardsBelowThreshold(value);
    if (value && !isNaN(value)) {
      calculateMgp(parseInt(value));
    } else {
      setMgpCalculation(null);
    }
  };

  const handleApplyMgp = async () => {
    if (!mgpCardsBelowThreshold || !mgpCalculation) return;
    
    const confirmed = window.confirm(
      `This will issue a refund of $${mgpCalculation.total_credit.toFixed(2)} to the customer. Continue?`
    );
    
    if (!confirmed) return;
    
    setMgpLoading(true);
    setMgpError(null);
    
    try {
      const response = await axios.post(
        `${API}/admin/submissions/${submissionId}/mgp/apply`,
        { cards_below_threshold: parseInt(mgpCardsBelowThreshold) }
      );
      
      if (response.data.success) {
        setActionMessage({
          type: 'success',
          text: response.data.message
        });
        closeMgpModal();
        await fetchSubmission();
      }
    } catch (err) {
      console.error('Apply MGP failed:', err);
      setMgpError(err.response?.data?.detail || 'Failed to apply MGP');
    } finally {
      setMgpLoading(false);
    }
  };

  const closeMgpModal = () => {
    setShowMgpModal(false);
    setMgpCardsBelowThreshold('');
    setMgpCalculation(null);
    setMgpError(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatCurrency = (amount) => {
    if (amount == null) return '$0.00';
    return `$${Number(amount).toFixed(2)}`;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 animate-spin text-[#003594]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-8">
          <div className="bg-white rounded-xl border border-red-200 p-8 text-center">
            <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-gray-900 mb-2">Error</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Link to="/admin">
              <Button className="bg-[#003594] hover:bg-[#002554]">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin Panel
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (!submission) return null;

  const statusConfig = getStatusConfig(submission.status);
  const StatusIcon = statusConfig.icon;
  
  // Calculate order total dynamically
  const gradingCost = submission.grading_cost || (submission.total_price - (submission.shipping_cost || 0) - (submission.insurance_cost || 0));
  const shippingCost = submission.shipping_cost || 15;
  const insuranceCost = submission.insurance_cost || 0;
  const voucherDiscount = submission.voucher_discount || 0;
  const orderTotal = submission.total_price || (gradingCost + shippingCost + insuranceCost);

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
          <Link to="/admin" className="hover:text-[#003594]">Admin Dashboard</Link>
          <ChevronRight className="w-4 h-4" />
          <span className="text-gray-900">Submission Details</span>
        </nav>

        {/* Header */}
        <div className="bg-white rounded-xl border border-gray-200 p-6 mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-[#003594] mb-2">
                {submission.submission_number}
              </h1>
              <div className="flex flex-wrap items-center gap-3">
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${statusConfig.color}`}>
                  <StatusIcon className="w-4 h-4" />
                  {statusConfig.label}
                </span>
                <span className="text-gray-500 flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(submission.created_at)}
                </span>
              </div>
            </div>
            <Link to="/admin">
              <Button variant="outline">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Admin
              </Button>
            </Link>
          </div>
        </div>

        {/* Action Message */}
        {actionMessage && (
          <div className={`mb-6 p-4 rounded-lg border ${
            actionMessage.type === 'success' 
              ? 'bg-green-50 border-green-200 text-green-700' 
              : 'bg-red-50 border-red-200 text-red-700'
          }`}>
            <div className="flex items-center gap-2">
              {actionMessage.type === 'success' ? (
                <CheckCircle className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
              {actionMessage.text}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column */}
          <div className="lg:col-span-2 space-y-6">
            {/* Customer Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <User className="w-5 h-5 text-[#003594]" />
                Customer Information
              </h2>
              {submission.customer ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500 w-24">Name:</span>
                    <span className="font-medium text-gray-900">{submission.customer.name}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-gray-500 w-24">Email:</span>
                    <a href={`mailto:${submission.customer.email}`} className="text-[#003594] hover:underline flex items-center gap-1">
                      <Mail className="w-4 h-4" />
                      {submission.customer.email}
                    </a>
                  </div>
                  {submission.customer.phone && (
                    <div className="flex items-center gap-3">
                      <span className="text-gray-500 w-24">Phone:</span>
                      <a href={`tel:${submission.customer.phone}`} className="text-[#003594] hover:underline flex items-center gap-1">
                        <Phone className="w-4 h-4" />
                        {submission.customer.phone}
                      </a>
                    </div>
                  )}
                  
                  {/* Shipping Address */}
                  {submission.shipping_address && (
                    <div className="mt-4 pt-4 border-t border-gray-100">
                      <div className="flex items-start gap-3">
                        <MapPin className="w-5 h-5 text-gray-400 mt-0.5" />
                        <div>
                          <p className="text-sm text-gray-500 mb-1">Return Shipping Address</p>
                          <p className="font-medium">{submission.shipping_address.name}</p>
                          <p className="text-gray-700">{submission.shipping_address.street_address}</p>
                          <p className="text-gray-700">
                            {submission.shipping_address.city}, {submission.shipping_address.state} {submission.shipping_address.zip_code}
                          </p>
                          {submission.shipping_address.phone && (
                            <p className="text-gray-500 text-sm mt-1">{submission.shipping_address.phone}</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-gray-500">Customer information not available</p>
              )}
            </div>

            {/* Submission Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Package className="w-5 h-5 text-[#003594]" />
                Submission Summary
              </h2>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Total Cards</p>
                  <p className="text-xl font-bold text-gray-900">{submission.card_count} cards</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Price per Card</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(submission.price_per_card)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Grading Cost</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(gradingCost)}</p>
                </div>
                <div className="bg-gray-50 rounded-lg p-3">
                  <p className="text-sm text-gray-500">Return Shipping</p>
                  <p className="text-xl font-bold text-gray-900">{formatCurrency(shippingCost)}</p>
                  {submission.package_weight_lbs > 0 && (
                    <p className="text-xs text-gray-400">{submission.package_weight_lbs} lbs</p>
                  )}
                </div>
                {submission.insurance_cost > 0 && (
                  <div className="bg-gray-50 rounded-lg p-3">
                    <p className="text-sm text-gray-500">Insurance</p>
                    <p className="text-xl font-bold text-gray-900">{formatCurrency(insuranceCost)}</p>
                  </div>
                )}
                <div className={`bg-[#003594]/10 rounded-lg p-3 ${submission.insurance_cost > 0 ? '' : 'col-span-2'}`}>
                  <p className="text-sm text-[#003594]">Order Total</p>
                  <p className="text-2xl font-bold text-[#003594]">{formatCurrency(orderTotal)}</p>
                  {voucherDiscount > 0 && (
                    <p className="text-xs text-green-600 mt-1">
                      Includes -{formatCurrency(voucherDiscount)} discount
                    </p>
                  )}
                </div>
                {submission.estimated_submission_value && (
                  <div className="bg-amber-50 rounded-lg p-3 col-span-2">
                    <p className="text-sm text-amber-700">Declared Value (for insurance)</p>
                    <p className="text-xl font-bold text-amber-700">
                      ${Number(submission.estimated_submission_value).toLocaleString()}
                    </p>
                    {submission.insurance_cost === 0 && (
                      <p className="text-xs text-amber-600 mt-1">Insurance included (up to $100)</p>
                    )}
                  </div>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t border-gray-100 grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-gray-500">Pricing Tier:</span>
                  <span className="ml-2 font-medium">{submission.pricing_tier}</span>
                </div>
                <div>
                  <span className="text-gray-500">Date Submitted:</span>
                  <span className="ml-2 font-medium">{formatDate(submission.created_at)}</span>
                </div>
                {submission.customer?.payment_method_brand && (
                  <div className="col-span-2">
                    <span className="text-gray-500">Payment Method:</span>
                    <span className="ml-2 font-medium">
                      [{submission.customer.payment_method_brand}] ending in {submission.customer.payment_method_last4}
                    </span>
                  </div>
                )}
              </div>

              {/* Adjustment Info */}
              {submission.adjustments && submission.adjustments.length > 0 && (
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      {submission.adjustments[0].adjustment_type === 'quantity_decreased' ? (
                        <TrendingDown className="w-5 h-5 text-amber-600" />
                      ) : (
                        <TrendingUp className="w-5 h-5 text-amber-600" />
                      )}
                      <span className="font-medium text-amber-800">Quantity Adjusted</span>
                    </div>
                    <div className="text-sm text-amber-700 space-y-1">
                      <p>
                        <span className="font-medium">Original:</span> {submission.adjustments[0].original_card_count} cards → 
                        <span className="font-medium ml-1">Adjusted:</span> {submission.adjustments[0].new_card_count} cards
                      </p>
                      <p><span className="font-medium">Reason:</span> {submission.adjustments[0].reason}</p>
                      <p className="text-xs text-amber-600">
                        Adjusted by {submission.adjustments[0].adjusted_by_name} on {formatDate(submission.adjustments[0].created_at)}
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Card Details */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#003594]" />
                Card Details
              </h2>
              {submission.cards && submission.cards.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-gray-50 border-b border-gray-200">
                        <th className="text-left py-3 px-3 font-medium text-gray-600">#</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Year</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Set/Brand</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Card #</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Player/Subject</th>
                        <th className="text-left py-3 px-3 font-medium text-gray-600">Insert/Parallel</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submission.cards.map((card, index) => (
                        <tr key={card.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                          <td className="py-3 px-3 text-gray-500">{card.position_number || index + 1}</td>
                          <td className="py-3 px-3">{card.year || '-'}</td>
                          <td className="py-3 px-3">{card.set_brand || '-'}</td>
                          <td className="py-3 px-3">{card.card_number || '-'}</td>
                          <td className="py-3 px-3">{card.player_name || '-'}</td>
                          <td className="py-3 px-3">{card.insert_parallel || '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-lg p-6 text-center">
                  <p className="text-gray-500 italic">
                    Card details not provided by customer. Details will be researched upon receipt.
                  </p>
                </div>
              )}
            </div>

            {/* Status Timeline - Structured Workflow */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5 text-[#003594]" />
                Order Progress
              </h2>
              
              {/* Admin Timeline Steps */}
              {(() => {
                // Define the admin-facing workflow steps (more detailed)
                const workflowSteps = [
                  { 
                    id: 'submitted', 
                    name: 'Submission Created', 
                    description: 'Order submitted by customer',
                    timestamp: submission.created_at
                  },
                  { 
                    id: 'awaiting', 
                    name: 'Awaiting Receipt', 
                    description: 'Waiting for cards to arrive',
                    timestamp: null
                  },
                  { 
                    id: 'received', 
                    name: 'Cards Received', 
                    description: 'Cards arrived at grading center',
                    timestamp: submission.received_at
                  },
                  { 
                    id: 'payment', 
                    name: 'Payment Processed', 
                    description: 'Payment collected from customer',
                    timestamp: submission.payment_collected_at
                  },
                  { 
                    id: 'grading', 
                    name: 'Grading', 
                    description: 'Cards being professionally graded',
                    timestamp: submission.grading_completed_at
                  },
                  { 
                    id: 'encapsulation', 
                    name: 'ID & Encapsulation', 
                    description: 'Cards encased and labeled',
                    timestamp: submission.encapsulation_completed_at
                  },
                  { 
                    id: 'shipped', 
                    name: 'Shipped', 
                    description: 'Package dispatched to customer',
                    timestamp: submission.shipped_at
                  },
                  { 
                    id: 'complete', 
                    name: 'Complete', 
                    description: 'Order delivered',
                    timestamp: submission.completed_at
                  }
                ];

                // Determine current step index based on status
                const currentStatus = submission.status;
                const statusToStepIndex = {
                  'awaiting_shipment': 1, // "Awaiting Receipt" is current
                  'received': 3, // "Payment Processed" is current (or payment failed)
                  'payment_processed': 4, // "Grading" is current
                  'grading_complete': 5, // "ID & Encapsulation" is current
                  'id_and_encapsulation': 6, // "Shipped" is current
                  'shipped': 7, // "Complete" is current
                  'complete': 8 // All complete
                };
                
                const currentStepIndex = statusToStepIndex[currentStatus] ?? 0;
                const isPaymentFailed = submission.payment_failed && currentStatus === 'received';

                // Determine which steps are completed
                const getStepState = (stepIndex) => {
                  if (stepIndex === 0) return 'completed'; // Created always completed
                  if (stepIndex === 1 && currentStepIndex > 1) return 'completed'; // Awaiting receipt
                  if (stepIndex === 2 && ['received', 'payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'].includes(currentStatus)) return 'completed';
                  if (stepIndex === 3 && ['payment_processed', 'grading_complete', 'id_and_encapsulation', 'shipped', 'complete'].includes(currentStatus)) return 'completed';
                  if (stepIndex === 4 && ['grading_complete', 'id_and_encapsulation', 'shipped', 'complete'].includes(currentStatus)) return 'completed';
                  if (stepIndex === 5 && ['id_and_encapsulation', 'shipped', 'complete'].includes(currentStatus)) return 'completed';
                  if (stepIndex === 6 && ['shipped', 'complete'].includes(currentStatus)) return 'completed';
                  if (stepIndex === 7 && currentStatus === 'complete') return 'completed';
                  
                  if (stepIndex === currentStepIndex) return 'current';
                  if (stepIndex === 3 && isPaymentFailed) return 'failed'; // Payment step failed
                  
                  return 'pending';
                };

                return (
                  <div className="space-y-0">
                    {workflowSteps.map((step, index) => {
                      const stepState = getStepState(index);
                      const isPaymentStep = step.id === 'payment';
                      const showPaymentFailed = isPaymentStep && isPaymentFailed;
                      
                      return (
                        <div key={step.id} className="flex items-start gap-4">
                          {/* Step indicator */}
                          <div className="relative flex flex-col items-center">
                            <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium z-10 ${
                              stepState === 'completed' 
                                ? 'bg-green-500 text-white' 
                                : stepState === 'current'
                                  ? 'bg-[#003594] text-white ring-4 ring-[#003594]/20'
                                  : stepState === 'failed'
                                    ? 'bg-red-500 text-white ring-4 ring-red-200'
                                    : 'bg-gray-200 text-gray-500'
                            }`}>
                              {stepState === 'completed' ? (
                                <CheckCircle className="w-5 h-5" />
                              ) : stepState === 'failed' ? (
                                <XCircle className="w-5 h-5" />
                              ) : (
                                index + 1
                              )}
                            </div>
                            {/* Connector line */}
                            {index < workflowSteps.length - 1 && (
                              <div className={`w-0.5 h-10 ${
                                stepState === 'completed' ? 'bg-green-500' : 
                                stepState === 'failed' ? 'bg-red-300' : 'bg-gray-200'
                              }`}></div>
                            )}
                          </div>
                          
                          {/* Step content */}
                          <div className={`flex-1 pb-6 ${index === workflowSteps.length - 1 ? 'pb-0' : ''}`}>
                            <p className={`font-medium ${
                              stepState === 'completed' 
                                ? 'text-green-700' 
                                : stepState === 'current' 
                                  ? 'text-[#003594]' 
                                  : stepState === 'failed'
                                    ? 'text-red-700'
                                    : 'text-gray-400'
                            }`}>
                              {showPaymentFailed ? 'Payment Failed' : step.name}
                            </p>
                            <p className={`text-sm ${
                              stepState === 'completed' || stepState === 'current' 
                                ? 'text-gray-600' 
                                : stepState === 'failed'
                                  ? 'text-red-600'
                                  : 'text-gray-400'
                            }`}>
                              {showPaymentFailed ? 'Retrying automatically...' : step.description}
                            </p>
                            
                            {/* Timestamp or status */}
                            {stepState === 'completed' && step.timestamp && (
                              <p className="text-xs text-gray-500 mt-1">
                                {formatDate(step.timestamp)}
                              </p>
                            )}
                            {stepState === 'current' && (
                              <p className="text-xs text-[#003594] font-medium mt-1">In Progress</p>
                            )}
                            {stepState === 'failed' && (
                              <p className="text-xs text-red-600 font-medium mt-1">
                                Attempt {submission.payment_attempt_count || 1}/4 
                                {submission.next_payment_retry_at && ` • Next retry: ${formatDate(submission.next_payment_retry_at)}`}
                              </p>
                            )}
                            {stepState === 'pending' && (
                              <p className="text-xs text-gray-400 mt-1">Pending</p>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                );
              })()}
            </div>

            {/* Activity Log (Collapsible - Raw Status History) */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <details className="group">
                <summary className="flex items-center justify-between cursor-pointer list-none">
                  <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#003594]" />
                    Activity Log
                  </h2>
                  <ChevronRight className="w-5 h-5 text-gray-400 transition-transform group-open:rotate-90" />
                </summary>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  {submission.status_history && submission.status_history.length > 0 ? (
                    <div className="space-y-3 max-h-64 overflow-y-auto">
                      {submission.status_history.map((entry, index) => (
                        <div key={entry.id || index} className="flex gap-3 text-sm">
                          <div className="w-2 h-2 rounded-full bg-gray-400 mt-1.5 flex-shrink-0"></div>
                          <div className="flex-grow">
                            <p className="text-gray-700">
                              {entry.old_status ? (
                                <>
                                  Status changed from <span className="font-medium">{getStatusConfig(entry.old_status).label}</span> to <span className="font-medium">{getStatusConfig(entry.new_status).label}</span>
                                </>
                              ) : (
                                <span className="font-medium">{getStatusConfig(entry.new_status).label}</span>
                              )}
                              {entry.created_by_name && <span className="text-gray-500"> by {entry.created_by_name}</span>}
                            </p>
                            <p className="text-xs text-gray-400">{formatDate(entry.created_at)}</p>
                            {entry.note && (
                              <p className="text-xs text-gray-600 mt-1 bg-gray-50 rounded px-2 py-1">
                                {entry.note}
                              </p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-gray-500 text-center py-2 text-sm">No activity recorded</p>
                  )}
                </div>
              </details>
            </div>
          </div>

          {/* Right Column - Actions */}
          <div className="space-y-6">
            {/* Status Management */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <Shield className="w-5 h-5 text-[#003594]" />
                Status Management
              </h2>
              
              <div className="mb-4">
                <p className="text-sm text-gray-500 mb-2">Current Status</p>
                <span className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-medium border ${statusConfig.color}`}>
                  <StatusIcon className="w-4 h-4" />
                  {statusConfig.label}
                </span>
              </div>

              <div className="space-y-4">
                {/* Status: Awaiting Shipment */}
                {submission.status === 'awaiting_shipment' && (
                  <div>
                    <Button 
                      onClick={handleMarkReceived}
                      disabled={actionLoading === 'mark-received'}
                      className="w-full bg-[#003594] hover:bg-[#002554]"
                    >
                      {actionLoading === 'mark-received' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Package className="w-4 h-4 mr-2" />
                      )}
                      Mark as Received
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Click when cards arrive at facility.
                    </p>
                  </div>
                )}

                {/* Status: Received */}
                {submission.status === 'received' && (
                  <div className="space-y-3">
                    {submission.payment_failed && (
                      <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                        <p className="text-sm font-medium text-red-700 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4" />
                          Payment Failed
                        </p>
                        {submission.payment_failure_reason && (
                          <p className="text-sm text-red-600 mt-2">
                            <strong>Reason:</strong> {submission.payment_failure_reason}
                          </p>
                        )}
                        {submission.stripe_decline_code && (
                          <p className="text-xs text-red-500 mt-1 font-mono bg-red-100 px-2 py-1 rounded inline-block">
                            Stripe Code: {submission.stripe_decline_code}
                          </p>
                        )}
                        <div className="mt-2 pt-2 border-t border-red-200 text-xs text-red-500 space-y-1">
                          <p>Attempt {submission.payment_attempt_count || 1} of 4</p>
                          {submission.last_payment_attempt_at && (
                            <p>Last attempt: {formatDate(submission.last_payment_attempt_at)}</p>
                          )}
                          {submission.next_payment_retry_at && (
                            <p>Auto-retry: {formatDate(submission.next_payment_retry_at)}</p>
                          )}
                        </div>
                        {submission.requires_admin_attention && (
                          <p className="text-xs text-red-700 font-medium mt-2 bg-red-100 p-2 rounded">
                            ⚠️ REQUIRES ADMIN ATTENTION - Max retries reached. Contact customer directly.
                          </p>
                        )}
                      </div>
                    )}
                    <Button 
                      onClick={submission.payment_failed ? handleRetryPayment : handleProcessPayment}
                      disabled={actionLoading === 'process-payment' || actionLoading === 'retry-payment'}
                      className="w-full bg-green-600 hover:bg-green-700"
                    >
                      {(actionLoading === 'process-payment' || actionLoading === 'retry-payment') ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CreditCard className="w-4 h-4 mr-2" />
                      )}
                      {submission.payment_failed ? 'Retry Payment Now' : 'Process Payment'}
                    </Button>
                    <Button 
                      onClick={openAdjustModal}
                      variant="outline"
                      className="w-full"
                    >
                      <Edit3 className="w-4 h-4 mr-2" />
                      Adjust Quantity
                    </Button>
                    <p className="text-xs text-gray-500">
                      {submission.payment_failed 
                        ? 'Manual retry does not count against auto-retry limit.'
                        : 'Process payment to continue. Use "Adjust Quantity" if card count differs.'
                      }
                    </p>
                  </div>
                )}

                {/* Status: Payment Processed */}
                {submission.status === 'payment_processed' && (
                  <div>
                    <Button 
                      onClick={handleMarkGradingComplete}
                      disabled={actionLoading === 'mark-grading-complete'}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      {actionLoading === 'mark-grading-complete' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Mark Grading Complete
                    </Button>
                    <p className="text-xs text-gray-500 mt-2">
                      Click when grading is finished.
                    </p>
                  </div>
                )}

                {/* MGP Adjustment Button - Show for grading_complete if MGP enabled and not yet applied */}
                {submission.mgp_enabled && !submission.mgp_applied_at && 
                 (submission.status === 'grading_complete' || submission.status === 'id_and_encapsulation') && (
                  <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <Shield className="w-5 h-5 text-purple-600" />
                      <span className="font-semibold text-purple-900">Minimum Grade Protection</span>
                    </div>
                    <p className="text-sm text-purple-700 mb-3">
                      This dealer submission has MGP. Apply credits for cards grading below 9.
                    </p>
                    <Button
                      onClick={() => setShowMgpModal(true)}
                      className="w-full bg-purple-600 hover:bg-purple-700"
                    >
                      <Shield className="w-4 h-4 mr-2" />
                      Apply MGP Adjustment
                    </Button>
                  </div>
                )}

                {/* MGP Already Applied */}
                {submission.mgp_enabled && submission.mgp_applied_at && (
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <CheckCircle className="w-5 h-5 text-green-600" />
                      <span className="font-semibold text-green-900">MGP Applied</span>
                    </div>
                    <div className="text-sm text-green-700 space-y-1">
                      <p>Cards below 9: {submission.mgp_cards_below_threshold}</p>
                      <p>Free allowance: {submission.mgp_free_cards}</p>
                      <p>MGP overage: {submission.mgp_billable_cards} × $1 = ${submission.mgp_charge?.toFixed(2)}</p>
                      <p className="font-semibold pt-1 border-t border-green-200">
                        Refund issued: ${submission.mgp_credit_amount?.toFixed(2)}
                      </p>
                      <p className="text-xs text-green-500">
                        Applied on {formatDate(submission.mgp_applied_at)}
                      </p>
                    </div>
                  </div>
                )}

                {/* Status: Grading Complete or ID & Encapsulation */}
                {(submission.status === 'grading_complete' || submission.status === 'id_and_encapsulation') && (
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Tracking Number *
                      </label>
                      <input
                        type="text"
                        value={trackingNumber}
                        onChange={(e) => setTrackingNumber(e.target.value)}
                        placeholder="Enter tracking number"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003594] focus:border-transparent"
                      />
                    </div>
                    <Button 
                      onClick={handleMarkShipped}
                      disabled={actionLoading === 'mark-shipped' || !trackingNumber.trim()}
                      className="w-full bg-green-600 hover:bg-green-700 disabled:bg-gray-300"
                    >
                      {actionLoading === 'mark-shipped' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <Truck className="w-4 h-4 mr-2" />
                      )}
                      Mark as Shipped
                    </Button>
                    <p className="text-xs text-gray-500">
                      Enter tracking number to mark as shipped.
                    </p>
                  </div>
                )}

                {/* Status: Shipped */}
                {submission.status === 'shipped' && (
                  <div className="space-y-3">
                    <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                      <p className="text-sm font-medium text-green-700">Shipped</p>
                      <p className="text-sm text-green-600 mt-1">
                        Tracking: {submission.tracking_number}
                      </p>
                      <p className="text-xs text-green-500 mt-1">
                        Shipped on {formatDate(submission.shipped_at)}
                      </p>
                    </div>
                    <Button 
                      onClick={handleMarkComplete}
                      disabled={actionLoading === 'mark-complete'}
                      variant="outline"
                      className="w-full"
                    >
                      {actionLoading === 'mark-complete' ? (
                        <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4 mr-2" />
                      )}
                      Mark as Complete
                    </Button>
                    <p className="text-xs text-gray-500">
                      Mark as delivered to customer.
                    </p>
                  </div>
                )}

                {/* Status: Complete */}
                {submission.status === 'complete' && (
                  <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                    <CheckCircle className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    <p className="font-medium text-gray-900">Order Complete</p>
                    <p className="text-sm text-gray-500">This submission has been completed.</p>
                  </div>
                )}
              </div>
            </div>

            {/* Payment Information */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5 text-[#003594]" />
                Payment Details
              </h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-gray-500">Status:</span>
                  <span className={`font-medium ${
                    submission.payment_collected_at ? 'text-green-600' : 
                    submission.payment_failed ? 'text-red-600' : 'text-yellow-600'
                  }`}>
                    {submission.payment_collected_at ? 'Processed' : 
                     submission.payment_failed ? 'Failed' : 'Pending'}
                  </span>
                </div>
                {submission.payment_collected_at && (
                  <div className="flex justify-between">
                    <span className="text-gray-500">Processed:</span>
                    <span>{formatDate(submission.payment_collected_at)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-gray-500">Amount:</span>
                  <span className="font-medium">{formatCurrency(orderTotal)}</span>
                </div>
                {voucherDiscount > 0 && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount Applied:</span>
                    <span className="font-medium">-{formatCurrency(voucherDiscount)}</span>
                  </div>
                )}
                
                {/* Payment Failure Details - Prominent Section for Admins */}
                {submission.payment_failed && (
                  <div className="mt-4 pt-4 border-t border-red-200 bg-red-50 -mx-6 -mb-6 p-4 rounded-b-xl">
                    <h3 className="text-sm font-semibold text-red-800 mb-3 flex items-center gap-2">
                      <AlertCircle className="w-4 h-4" />
                      Payment Failure Details
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div>
                        <span className="text-red-700 font-medium">Error Reason:</span>
                        <p className="text-red-600 mt-1">{submission.payment_failure_reason || 'Unknown error'}</p>
                      </div>
                      {submission.stripe_decline_code && (
                        <div>
                          <span className="text-red-700 font-medium">Stripe Decline Code:</span>
                          <code className="ml-2 px-2 py-1 bg-red-100 text-red-800 rounded font-mono text-xs">
                            {submission.stripe_decline_code}
                          </code>
                        </div>
                      )}
                      {submission.stripe_error_raw && (
                        <div>
                          <span className="text-red-700 font-medium">Raw Stripe Message:</span>
                          <p className="text-red-600 text-xs mt-1 font-mono bg-red-100 p-2 rounded">
                            {submission.stripe_error_raw}
                          </p>
                        </div>
                      )}
                      <div className="grid grid-cols-2 gap-2 pt-2 border-t border-red-200 text-xs">
                        <div>
                          <span className="text-red-600">Attempts:</span>
                          <span className="ml-1 font-medium">{submission.payment_attempt_count || 1} / 4</span>
                        </div>
                        {submission.last_payment_attempt_at && (
                          <div>
                            <span className="text-red-600">Last Attempt:</span>
                            <span className="ml-1">{formatDate(submission.last_payment_attempt_at)}</span>
                          </div>
                        )}
                        {submission.next_payment_retry_at && (
                          <div className="col-span-2">
                            <span className="text-red-600">Next Auto-Retry:</span>
                            <span className="ml-1">{formatDate(submission.next_payment_retry_at)}</span>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                )}
                
                {submission.stripe_payment_intent_id && (
                  <div className="pt-2 border-t border-gray-100">
                    <span className="text-gray-500 text-xs">Payment Intent ID:</span>
                    <p className="font-mono text-xs text-gray-600 break-all mt-1">
                      {submission.stripe_payment_intent_id}
                    </p>
                  </div>
                )}
              </div>
            </div>

            {/* Admin Notes */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                <FileText className="w-5 h-5 text-[#003594]" />
                Internal Notes
              </h2>
              <textarea
                value={adminNotes}
                onChange={(e) => {
                  setAdminNotes(e.target.value);
                  setNotesSaved(false);
                }}
                placeholder="Add internal notes about this submission..."
                className="w-full h-32 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003594] focus:border-transparent resize-none"
              />
              <div className="flex items-center justify-between mt-3">
                <p className="text-xs text-gray-500">
                  Notes are internal only and not visible to customers.
                </p>
                <Button 
                  onClick={handleSaveNotes}
                  disabled={actionLoading === 'save-notes'}
                  size="sm"
                  variant={notesSaved ? 'outline' : 'default'}
                  className={notesSaved ? 'text-green-600 border-green-300' : 'bg-[#003594] hover:bg-[#002554]'}
                >
                  {actionLoading === 'save-notes' ? (
                    <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                  ) : notesSaved ? (
                    <CheckCircle className="w-4 h-4 mr-1" />
                  ) : (
                    <Save className="w-4 h-4 mr-1" />
                  )}
                  {notesSaved ? 'Saved' : 'Save Notes'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Adjust Quantity Modal */}
      {showAdjustModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Adjust Submission Quantity</h3>
                <button onClick={closeAdjustModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>

            <div className="p-6 space-y-6">
              {/* Current Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h4 className="text-sm font-medium text-gray-700 mb-3">Current Order</h4>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div>
                    <span className="text-gray-500">Quantity:</span>
                    <span className="ml-2 font-medium">{submission.card_count} cards</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Price/Card:</span>
                    <span className="ml-2 font-medium">{formatCurrency(submission.price_per_card)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Tier:</span>
                    <span className="ml-2 font-medium">{submission.pricing_tier}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Grading:</span>
                    <span className="ml-2 font-medium">{formatCurrency(gradingCost)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Shipping:</span>
                    <span className="ml-2 font-medium">{formatCurrency(shippingCost)}</span>
                    {submission.package_weight_lbs > 0 && (
                      <span className="text-xs text-gray-400 ml-1">({submission.package_weight_lbs} lbs)</span>
                    )}
                  </div>
                  {submission.insurance_cost > 0 && (
                    <div>
                      <span className="text-gray-500">Insurance:</span>
                      <span className="ml-2 font-medium">{formatCurrency(insuranceCost)}</span>
                    </div>
                  )}
                  {voucherDiscount > 0 && (
                    <div className="text-green-600">
                      <span>Discount Applied:</span>
                      <span className="ml-2 font-medium">-{formatCurrency(voucherDiscount)}</span>
                    </div>
                  )}
                  <div className="col-span-2 border-t border-gray-200 pt-2 mt-1">
                    <span className="text-gray-500">Total:</span>
                    <span className="ml-2 font-bold">{formatCurrency(orderTotal)}</span>
                  </div>
                </div>
              </div>

              {/* New Quantity Input */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Actual cards received *
                </label>
                <input
                  type="number"
                  min="0"
                  max="1000"
                  value={adjustNewCount}
                  onChange={(e) => handleAdjustCountChange(e.target.value)}
                  placeholder={submission.card_count?.toString()}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003594] focus:border-transparent"
                />
              </div>

              {/* Reason Dropdown */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Reason for adjustment *
                </label>
                <select
                  value={adjustReason}
                  onChange={(e) => setAdjustReason(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003594] focus:border-transparent"
                >
                  <option value="">Select a reason...</option>
                  {ADJUSTMENT_REASONS.map((reason) => (
                    <option key={reason} value={reason}>{reason}</option>
                  ))}
                </select>
              </div>

              {/* Additional Notes */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Additional notes (optional)
                </label>
                <textarea
                  value={adjustNotes}
                  onChange={(e) => setAdjustNotes(e.target.value)}
                  placeholder="Add any additional details about this adjustment..."
                  className="w-full h-20 px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#003594] focus:border-transparent resize-none"
                />
              </div>

              {/* Calculated New Pricing */}
              {adjustCalculation && (
                <div className={`rounded-lg p-4 ${
                  adjustCalculation.difference < 0 
                    ? 'bg-orange-50 border border-orange-200' 
                    : 'bg-green-50 border border-green-200'
                }`}>
                  <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                    {adjustCalculation.difference < 0 ? (
                      <>
                        <TrendingDown className="w-4 h-4 text-orange-600" />
                        <span className="text-orange-800">Adjusted Pricing (Charging Less)</span>
                      </>
                    ) : (
                      <>
                        <TrendingUp className="w-4 h-4 text-green-600" />
                        <span className="text-green-800">Adjusted Pricing (Charging More)</span>
                      </>
                    )}
                  </h4>
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">New Quantity:</span>
                      <span className="ml-2 font-medium">{adjustCalculation.new.card_count} cards</span>
                    </div>
                    <div>
                      <span className="text-gray-500">New Price/Card:</span>
                      <span className="ml-2 font-medium">{formatCurrency(adjustCalculation.new.price_per_card)}</span>
                    </div>
                    {adjustCalculation.tier_changed && (
                      <div className="col-span-2">
                        <span className="text-gray-500">New Tier:</span>
                        <span className="ml-2 font-medium text-amber-700">
                          {adjustCalculation.original.pricing_tier} → {adjustCalculation.new.pricing_tier}
                        </span>
                      </div>
                    )}
                    
                    {/* Grading Cost */}
                    <div>
                      <span className="text-gray-500">Grading:</span>
                      <span className="ml-2 font-medium">{formatCurrency(adjustCalculation.new.grading_cost)}</span>
                    </div>
                    
                    {/* Shipping Cost with change indicator */}
                    <div>
                      <span className="text-gray-500">Shipping:</span>
                      <span className="ml-2 font-medium">{formatCurrency(adjustCalculation.new.shipping_cost)}</span>
                      {adjustCalculation.shipping_changed && (
                        <span className="text-xs text-gray-400 ml-1">
                          ({adjustCalculation.new.package_weight_lbs} lbs)
                        </span>
                      )}
                    </div>
                    
                    {/* Insurance (if any) */}
                    {adjustCalculation.new.insurance_cost > 0 && (
                      <div className="col-span-2">
                        <span className="text-gray-500">Insurance:</span>
                        <span className="ml-2 font-medium">{formatCurrency(adjustCalculation.new.insurance_cost)}</span>
                      </div>
                    )}
                    
                    <div>
                      <span className="text-gray-500">New Total:</span>
                      <span className="ml-2 font-bold">{formatCurrency(adjustCalculation.new.total_price)}</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Difference:</span>
                      <span className={`ml-2 font-bold ${
                        adjustCalculation.difference < 0 ? 'text-orange-600' : 'text-green-600'
                      }`}>
                        {adjustCalculation.difference < 0 ? '-' : '+'}
                        {formatCurrency(Math.abs(adjustCalculation.difference))}
                      </span>
                    </div>
                  </div>
                  {adjustCalculation.shipping_changed && (
                    <p className="mt-2 text-xs text-gray-600 border-t border-gray-200 pt-2">
                      📦 Shipping cost adjusted based on new package weight ({adjustCalculation.new.package_weight_lbs} lbs)
                    </p>
                  )}
                  {adjustCalculation.is_significant_change && (
                    <p className="mt-2 text-xs text-amber-700 font-medium">
                      ⚠️ This is a significant change ({adjustCalculation.percentage_change}%). Confirmation required.
                    </p>
                  )}
                </div>
              )}

              {/* Error Message */}
              {adjustError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {adjustError}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end">
              <Button variant="outline" onClick={closeAdjustModal} disabled={adjustLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleAdjustAndProcess}
                disabled={
                  adjustLoading || 
                  !adjustNewCount || 
                  !adjustReason || 
                  !adjustCalculation ||
                  parseInt(adjustNewCount) === submission?.card_count
                }
                className="bg-[#003594] hover:bg-[#002554]"
              >
                {adjustLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <CreditCard className="w-4 h-4 mr-2" />
                )}
                Adjust & Process Payment
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* MGP Modal */}
      {showMgpModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="p-6 border-b border-gray-200 bg-gradient-to-r from-purple-50 to-purple-100 rounded-t-xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-purple-600 p-2 rounded-lg">
                    <Shield className="w-5 h-5 text-white" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">Apply Minimum Grade Protection</h2>
                    <p className="text-sm text-gray-600">Submission #{submission?.submission_number}</p>
                  </div>
                </div>
                <button onClick={closeMgpModal} className="text-gray-400 hover:text-gray-600">
                  <X className="w-6 h-6" />
                </button>
              </div>
            </div>

            {/* Modal Body */}
            <div className="p-6 space-y-6">
              {/* Submission Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="text-gray-500">Total Cards:</span>
                    <span className="ml-2 font-semibold">{submission?.card_count}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Price/Card:</span>
                    <span className="ml-2 font-semibold">${submission?.price_per_card?.toFixed(2)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">Original Charge:</span>
                    <span className="ml-2 font-semibold">{formatCurrency(submission?.total_price)}</span>
                  </div>
                  <div>
                    <span className="text-gray-500">MGP Free Allowance:</span>
                    <span className="ml-2 font-semibold">{Math.floor(submission?.card_count * 0.1)} cards (10%)</span>
                  </div>
                </div>
              </div>

              {/* Input: Cards Below 9 */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Cards Graded Below 9 <span className="text-red-500">*</span>
                </label>
                <input
                  type="number"
                  min="0"
                  max={submission?.card_count}
                  value={mgpCardsBelowThreshold}
                  onChange={(e) => handleMgpCardsBelowChange(e.target.value)}
                  placeholder="Enter number of cards below grade 9"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg text-lg focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Enter the count of cards that graded below 9 and will not be slabbed.
                </p>
              </div>

              {/* Calculation Preview */}
              {mgpCalculation && (
                <div className="bg-purple-50 border-2 border-purple-200 rounded-lg p-4">
                  <h3 className="font-semibold text-purple-900 mb-3 flex items-center gap-2">
                    <DollarSign className="w-4 h-4" />
                    MGP Calculation Preview
                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cards below 9:</span>
                      <span className="font-medium">{mgpCalculation.cards_below_threshold}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Free MGP allowance (10%):</span>
                      <span className="font-medium text-green-600">{mgpCalculation.free_mgp_allowance} cards</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Billable MGP overage @ $1:</span>
                      <span className="font-medium">{mgpCalculation.billable_mgp_cards} × $1 = ${mgpCalculation.mgp_overage_charge.toFixed(2)}</span>
                    </div>
                    <div className="border-t border-purple-200 pt-2 mt-2">
                      <div className="flex justify-between">
                        <span className="text-gray-600">Credit for ungraded cards:</span>
                        <span className="font-medium">{mgpCalculation.cards_not_charged} × ${submission?.price_per_card?.toFixed(2)} = ${mgpCalculation.credit_for_ungraded.toFixed(2)}</span>
                      </div>
                    </div>
                    <div className="border-t border-purple-300 pt-3 mt-3">
                      <div className="flex justify-between text-lg">
                        <span className="font-semibold text-purple-900">Total Credit/Refund:</span>
                        <span className="font-bold text-green-600">-${mgpCalculation.total_credit.toFixed(2)}</span>
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-gray-600">Final charge:</span>
                        <span className="font-semibold">${mgpCalculation.final_charge.toFixed(2)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* No refund message */}
              {mgpCalculation && mgpCalculation.total_credit === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <p className="text-sm text-amber-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    No refund needed - all cards below 9 are within the free 10% allowance.
                  </p>
                </div>
              )}

              {/* Error Message */}
              {mgpError && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                  <p className="text-sm text-red-700 flex items-center gap-2">
                    <AlertCircle className="w-4 h-4" />
                    {mgpError}
                  </p>
                </div>
              )}
            </div>

            {/* Modal Actions */}
            <div className="p-6 border-t border-gray-200 flex gap-3 justify-end bg-gray-50 rounded-b-xl">
              <Button variant="outline" onClick={closeMgpModal} disabled={mgpLoading}>
                Cancel
              </Button>
              <Button 
                onClick={handleApplyMgp}
                disabled={mgpLoading || !mgpCardsBelowThreshold || !mgpCalculation}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {mgpLoading ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Shield className="w-4 h-4 mr-2" />
                )}
                Apply MGP & Process Refund
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminSubmissionDetailPage;
